import { Component, ComponentFactoryResolver, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Models } from '@ibexfusion/models';
import { MatDialog, MatDialogConfig, MatDialogRef } from '@angular/material/dialog';
import { SnackbarService } from '@services/snackbar/snackbar.service';
import { FormDirective } from '../../../directives/form/form.directive';
import { PageAction } from '../../../components/page-actions/page-actions.component';
import { OrganisationService } from '@services/organisations/organisation.service';
import { DefaultFormComponent } from '../../../components/default-form/default-form.component';
import { FormComponent } from '../../../components/default-form/form.component';
import { CustomerService } from '@services/customers/customer.service';
import { CustomerSelectionComponent } from '../../../components/popUpSelects/customer-selection/customer-selection.component';
import { OrganisationEraserComponent } from './organisation-eraser/organisation-eraser.component';
import { OrganisationSelectionComponent } from '../../../components/popUpSelects/organisation-selection/organisation-selection.component';

@Component({
    selector: 'app-organisation-edit',
    templateUrl: './organisation-edit.component.html',
    styleUrls: ['./organisation-edit.component.scss'],
    providers: [OrganisationService, CustomerService],
    standalone: false
})
export class OrganisationEditComponent implements OnInit {

  @ViewChild(FormDirective, { static: true }) formHost: FormDirective;

  pageActions: PageAction[];
  title = 'Edit Organisation: ';
  isLoading = true;
  boundOrganisation: Models.Organisation;
  organisationId: string;

  public columns = [
    { prop: 'company' },
    { prop: 'touch2015Count' },
    { validate: '_id', name: 'Imported', icon: 'check_box' }
  ];

  constructor(
    private componentFactoryResolver: ComponentFactoryResolver,
    private organisationService: OrganisationService,
    public customerService: CustomerService,
    private activatedRoute: ActivatedRoute,
    private snackbar: SnackbarService,
    private router: Router,
    private dialog: MatDialog,
  ) {
  }

  ngOnInit() {
    this.createPageAction();
    if (this.activatedRoute.snapshot.params['id']) {
      this.organisationId = this.activatedRoute.snapshot.params['id'];
      this.organisationService.get(this.activatedRoute.snapshot.params['id']).then(result => {
        this.boundOrganisation = result;
        this.title += this.boundOrganisation.name;
        this.isLoading = false;
        this.loadForm();
      });
    } else {
      this.boundOrganisation = <Models.Organisation>Models.ModelFactory.createModelInstanceByName('Organisation');
      this.title = 'Create new organisation';
      this.isLoading = false;
      this.loadForm();
    }
  }

  loadForm() {
    const formItem = DefaultFormComponent;

    const componentFactory = this.componentFactoryResolver.resolveComponentFactory(formItem);

    const viewContainerRef = this.formHost.viewContainerRef;
    viewContainerRef.clear();

    const componentRef = viewContainerRef.createComponent(componentFactory);
    (<FormComponent>componentRef.instance).entry = this.boundOrganisation;
  }

  onSave() {
    return this.boundOrganisation.validate(true).then(result => {
      if (result.success) {
        if (this.organisationId) {
          this.organisationService.update(this.organisationId, this.boundOrganisation).then(organisationResult => {
            this.snackbar.success('Organisation successfully updated');
          });
        } else {
          this.organisationService.create(this.boundOrganisation).then(organisationResult => {
            this.snackbar.success('Organisation successfully created');
          });
        }
      } else {
        this.snackbar.open('Could not create the organisation, please fill the form correctly');
      }
    });
  }

  assignCustomer(result) {
    if (result && result.customerType === 'organisationCustomer') {
      this.boundOrganisation.organisationCustomerId = result.customerId;
    } else if (result && result.customerType === 'ibex3') {
      this.boundOrganisation.ibex3customerId = result.customerId;
    }
  }

  createPageAction() {
    this.pageActions = [
      new PageAction({
        icon: 'edit',
        title: 'detail organisation',
        callback: () => {
          this.router.navigate(['organisations', this.activatedRoute.snapshot.params['id']])
        }
      }),
      new PageAction({
        icon: 'add',
        title: 'assign customer',
        callback: () => {
          let dialogRef: MatDialogRef<CustomerSelectionComponent>;
          const config: MatDialogConfig = {
            data: {
              ids: [this.boundOrganisation.organisationCustomerId, this.boundOrganisation.ibex3customerId]
            }
          };

          dialogRef = this.dialog.open(CustomerSelectionComponent, config);

          return dialogRef.afterClosed().toPromise().then(result => {
            this.assignCustomer(result);
          });
        }
      }),
      new PageAction({
        icon: 'add',
        title: 'assign parentOrganisation',
        callback: () => {
          let dialogRef: MatDialogRef<OrganisationSelectionComponent>;
          dialogRef = this.dialog.open(OrganisationSelectionComponent, {});
          return dialogRef.afterClosed().toPromise().then(result => {
            if (result) {
              this.boundOrganisation.ParentOrganisationID = result.OrganisationID;
            }
          });
        }
      }),
      new PageAction({
        icon: 'delete',
        title: 'Erase Organisation',
        callback: () => {
          const config: MatDialogConfig = <any>{ data: { org: this.boundOrganisation } };
          this.dialog.open(OrganisationEraserComponent, config);
        }
      })
    ]
  }
}
